import React from "react";
import classNames from "classnames";
import { Crown, Check, X } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import ProListingStar from "@/img/hotel_class.svg";

const OWNER_PLANS = [
  {
    id: "basic",
    name: "Básico",
    duration: "1 mes",
    type: "Particular",
    price: "600",
    originalPrice: null,
    badge: null,
    isHighlighted: false,
    highlightText: "Publica 1 propiedad",
    features: [
      { text: "Edita tu publicación sin límites", included: true },
      {
        text: "Sube imágenes ilimitadas y videos de tu propiedad",
        included: true,
      },
      {
        text: "Acceso a analítica\n(Obtén reportes de tu publicación)",
        included: false,
      },
      {
        text: "Contacto directo desde los resultados de búsqueda",
        included: false,
      },
    ],
    idPlan: "particular-basico",
    priceId: "price_1SnSdaHGBw2VKdDPGG3FKZr5",
  },
  {
    id: "featured",
    name: "Destacado",
    duration: "3 meses",
    type: "Particular",
    price: "1400",
    originalPrice: "1800",
    badge: "Mejor valor",
    isHighlighted: true,
    highlightText:
      "Publica 1 propiedad destacada, con alta visibilidad en los resultados de búsqueda",
    features: [
      { text: "Edita tu publicación sin límites", included: true },
      {
        text: "Sube imágenes ilimitadas y videos de tu propiedad",
        included: true,
      },
      {
        text: "Acceso a analítica\n(Obtén reportes de tu publicación)",
        included: true,
      },
      {
        text: "Contacto directo desde los resultados de búsqueda",
        included: true,
      },
    ],
    idPlan: "particular-destacado",
    priceId: "price_1SD8s7HGBw2VKdDP7zDW3lqZ",
  },
  {
    id: "standard",
    name: "Estándar",
    duration: "3 meses",
    type: "Particular",
    price: "1000",
    originalPrice: null,
    badge: null,
    isHighlighted: false,
    highlightText: "Publica 1 propiedad",
    features: [
      { text: "Edita tu publicación sin límites", included: true },
      {
        text: "Sube imágenes ilimitadas y videos de tu propiedad",
        included: true,
      },
      {
        text: "Acceso a analítica\n(Obtén reportes de tu publicación)",
        included: true,
      },
      {
        text: "Contacto directo desde los resultados de búsqueda",
        included: false,
      },
    ],
    idPlan: "particular-estandar",
    priceId: "price_1SD8srHGBw2VKdDPj6z2Q4Dh",
  },
];

const PlanBadge = ({ badge }) => {
  if (!badge) return null;

  return (
    <Badge className="px-3 py-1 text-sm  font-semibold text-white rounded-full flex items-center gap-1 bg-black">
      {badge === "Mejor valor" && <Crown className="w-20 h-20 " />}
      <span className="">{badge}</span>
    </Badge>
  );
};

const PlanHeader = ({ name, badge, isHighlighted }) => (
  <CardHeader
    className={classNames("rounded-t-xl my-4", {
      "bg-black text-white flex flex-row justify-between items-center":
        isHighlighted,
      "bg-[#F2F2F2]": !isHighlighted,
    })}
  >
    <h3
      className={classNames("text-xl font-semibold mb-0", {
        "text-white": isHighlighted,
        "text-gray-900": !isHighlighted,
      })}
    >
      {name}
    </h3>
    <PlanBadge badge={badge} />
  </CardHeader>
);

const PlanMeta = ({ duration, type }) => (
  <div className="flex items-center justify-between pb-4">
    <span className="text-gray-600">{duration}</span>
    <Badge
      variant="outline"
      className="text-xs px-2 py-1 text-gray-500 border-gray-500 rounded-full"
    >
      {type}
    </Badge>
  </div>
);

const FeatureList = ({ features }) => (
  <div className="space-y-3">
    {features.map((feature, index) => (
      <div key={index} className="flex items-start gap-2">
        {feature.included ? (
          <div className="w-5 h-5 rounded-full bg-black flex items-center justify-center flex-shrink-0 mt-0.5">
            <Check className="w-3 h-3 text-white" />
          </div>
        ) : (
          <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0 mt-0.5">
            <X className="w-3 h-3 text-gray-400" />
          </div>
        )}
        <span
          className={classNames("text-sm", {
            "text-gray-700": feature.included,
            "text-gray-400 line-through": !feature.included,
          })}
        >
          {feature.text}
        </span>
      </div>
    ))}
  </div>
);

export const PlanCard = ({ plan, planType, selectedPlan, onSelectPlan }) => {
  const {
    name,
    duration,
    type,
    price,
    originalPrice,
    annualFee,
    features,
    proListings,
    badge,
    isHighlighted,
    highlightText,
    idPlan,
  } = plan;

  const isOwnerPlan = planType === "owner";
  const isSelected = selectedPlan === idPlan;

  const handleClick = () => {
    if (onSelectPlan) {
      onSelectPlan(idPlan);
    }
  };

  return (
    <Card
      onClick={handleClick}
      className={classNames(
        "gap-0 rounded-xl border py-0 transition-all duration-300 cursor-pointer",
        "hover:scale-[1.02] hover:shadow-xl",
        {
          "border-gray-900 transform bg-black": isHighlighted,
          "border-gray-200 bg-[#F2F2F2] h-fit": !isHighlighted,
          "ring-4 ring-offset-4 ring-[#8B4AFF] shadow-2xl scale-[1.03]":
            isSelected,
          "hover:ring-2 hover:ring-offset-2 hover:ring-[#8B4AFF]": !isSelected,
        }
      )}
    >
      {isSelected && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
          <div className="px-4 py-1 rounded-full text-xs font-bold text-white shadow-lg bg-[#8B4AFF]">
            Seleccionado
          </div>
        </div>
      )}

      <PlanHeader name={name} badge={badge} isHighlighted={isHighlighted} />

      <CardContent
        className={classNames(
          "p-3 lg:p-6 rounded-t-2xl bg-white rounded-b-xl space-y-4 relative",
          {
            "shadow-[inset_0px_-50px_39px_-37px_#cfcfcf]": isHighlighted,
          }
        )}
      >
        <div>
          {type && <PlanMeta duration={duration} type={type} />}

          <div className="text-sm text-gray-600">
            <span
              className={classNames("text-xs", {
                "text-[#8B4AFF]": isHighlighted,
                "text-black": !isHighlighted,
              })}
            >
              {highlightText}
            </span>
          </div>
        </div>
        {price && (
          <div
            className={classNames("border border-[#AFBFBF] p-2 rounded-lg", {
              "bg-[#F2F2F2]": isHighlighted,
              "bg-white": !isHighlighted,
            })}
          >
            {isOwnerPlan && (
              <div className="mb-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold">${price}</span>
                  <span className="text-gray-600">MXN</span>
                  {originalPrice && (
                    <span className="text-gray-400 line-through text-sm">
                      ${originalPrice}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        {annualFee && (
          <div className="text-xs text-gray-500 text-[10px] w-full text-center">
            {annualFee}
          </div>
        )}

        <Separator className="bg-[#AFBFBF] h-px my-4" />
        <FeatureList features={features} />
        {proListings && (
          <div className="-mt-2">
            <img
              src={ProListingStar}
              alt="Pro Listing"
              className="w-6 h-6 inline-block mr-2"
            />
            <span className="text-sm font-bold">PRO Listing</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function Plans({ selectedPlan, onSelectPlan }) {
  return (
    <div className="w-full overflow-scroll max-h-[80vh]">
      <div className="flex justify-center w-full py-8">
        <div
          className={classNames(
            "grid grid-cols-1 gap-8 transform  w-full max-w-[400px] "
          )}
        >
          {OWNER_PLANS.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              planType="owner"
              selectedPlan={selectedPlan}
              onSelectPlan={onSelectPlan}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
